import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { z } from "zod";
import { TFunction } from "i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form.tsx";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert.tsx";
import { AlertCircle, LoaderCircle } from "lucide-react";
import { ComponentProps, useEffect, useMemo, useState } from "react";
import { resetPassword } from "@/api/authentication/resetPassword.ts";
import { Link } from "react-router";
import { setTitle } from "@/hooks/useHeader.ts";
import { useTranslation } from "@/i18n";

const createFormSchema = (t: TFunction) =>
  z.object({
    email: z.string().email({ message: t("validation.invalidEmail") }),
  });

type FormValues = z.infer<ReturnType<typeof createFormSchema>>;

const ForgotPassword = ({ className, ...props }: ComponentProps<"div">) => {
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { t } = useTranslation();
  const formSchema = useMemo(() => createFormSchema(t), [t]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = async (values: FormValues) => {
    setLoading(true);
    setError("");
    try {
      await resetPassword(values);
      setSuccess(true);
    } catch (e: unknown) {
      console.error(e);
      setError(t("auth.forgotPassword.error"));
    }
    setLoading(false);
  };

  useEffect(() => {
    setTitle(t("auth.forgotPassword.title"));
  }, [t]);

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">
            {t("auth.forgotPassword.title")}
          </CardTitle>
          <CardDescription className="text-md">
            {success
              ? t("auth.forgotPassword.success")
              : t("auth.forgotPassword.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="pb-6">
          {error && (
            <Alert variant="destructive" className="mx-auto my-4 w-fit">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{t("auth.forgotPassword.failed")}</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {success ? (
            <div>
              <div className="text-center text-sm">
                {t("auth.forgotPassword.goBackTo")}{" "}
                <Link to="/sign-in" className="underline underline-offset-4">
                  {t("auth.forgotPassword.signInLink")}
                </Link>
              </div>
            </div>
          ) : (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <div className="grid gap-6">
                  <div className="grid gap-6">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("form.email")}</FormLabel>
                          <FormControl>
                            <Input
                              placeholder={t("form.emailPlaceholder")}
                              {...field}
                              onChange={(e) => {
                                field.onChange(e);
                                setError(undefined);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button type="submit" className="w-full" disabled={loading}>
                      {t("auth.forgotPassword.submit")}{" "}
                      {loading && <LoaderCircle className="animate-spin" />}
                    </Button>
                  </div>
                  <div className="text-center text-sm">
                    {t("auth.forgotPassword.goBackTo")}{" "}
                    <Link
                      to="/sign-in"
                      className="underline underline-offset-4"
                    >
                      {t("auth.forgotPassword.signInLink")}
                    </Link>
                  </div>
                </div>
              </form>
            </Form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
